import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Alert } from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import api from '../../api/client';
import { setSessionTokens } from '../../api/session';
import { getApiBase, isNative, getTenantBase, setTenantBase, clearTenantBase } from '../../native/tenant';

export default function MdtLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState('');
  const [disclaimer, setDisclaimer] = useState('');

  // Native app: require a tenant host before login. Web build skips this.
  const [tenantInput, setTenantInput] = useState('');
  const [needsTenant, setNeedsTenant] = useState(isNative() && !getTenantBase());

  // TOTP state
  const [totpStep, setTotpStep] = useState<'login' | 'verify' | 'setup'>('login');
  const [preAuthToken, setPreAuthToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpQr, setTotpQr] = useState('');
  const [totpSecret, setTotpSecret] = useState('');

  useEffect(() => {
    if (needsTenant) return; // wait until a tenant host is set (native)
    const base = getApiBase();
    fetch(`${base}/api/system-config/logo`)
      .then((r) => { if (r.ok) setLogoUrl(`${base}/api/system-config/logo`); })
      .catch(() => {});
    fetch(`${base}/api/system-config/public`)
      .then((r) => r.json())
      .then((d) => { setTenantName(d.agency_name || d.jurisdiction_name || ''); setDisclaimer(d.login_disclaimer || ''); })
      .catch(() => {});
  }, [needsTenant]);

  const handleTenantSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const base = setTenantBase(tenantInput);
    if (!base) { setError('Enter a valid tenant address, e.g. mo-sffd.cad.watchcenter365.com'); return; }
    setNeedsTenant(false);
  };

  const handleLoginSuccess = (data: any) => {
    const { access_token, refresh_token, full_name, personnel_id } = data;
    setSessionTokens('mobile', access_token, refresh_token);
    localStorage.setItem('user', JSON.stringify({
      full_name,
      personnel_id,
      mobile_access: true,
      dispatch_access: false,
      admin_access: false,
      superadmin_access: false,
      username,
    }));
    window.location.href = '/mobile/mdt';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const r = await api.post('/auth/mobile-login', { username, password });
      const data = r.data;

      if (data.totp_required) {
        setPreAuthToken(data.pre_auth_token);
        setTotpStep('verify');
        return;
      }
      if (data.totp_setup_required) {
        setPreAuthToken(data.pre_auth_token);
        setTotpQr(data.totp_qr || '');
        setTotpSecret(data.totp_secret || '');
        setTotpStep('setup');
        return;
      }

      handleLoginSuccess(data);
    } catch {
      setError('Invalid username or password');
    }
  };

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post('/auth/mobile-verify-totp', {
        pre_auth_token: preAuthToken,
        totp_code: totpCode,
      });
      handleLoginSuccess(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid TOTP code');
    }
  };

  const handleTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post('/auth/mobile-setup-totp', {
        pre_auth_token: preAuthToken,
        totp_code: totpCode,
      });
      handleLoginSuccess(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid TOTP code');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#0a1929', py: 4, gap: 3 }}>
      <Paper sx={{ p: 4, width: 400 }}>
        {logoUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box component="img" src={logoUrl} alt="Agency Logo" sx={{ maxHeight: 100, maxWidth: 200, objectFit: 'contain' }} />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <LocalShipping sx={{ fontSize: 40, color: '#2196f3' }} />
        </Box>
        <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          Mobile Data Terminal
        </Typography>
        {tenantName && (
          <Typography variant="subtitle1" align="center" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
            {tenantName}
          </Typography>
        )}
        <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
          WatchCenter CAD — Field Login
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {needsTenant && (
          <form onSubmit={handleTenantSubmit}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Enter your agency's WatchCenter address to connect this device.
            </Typography>
            <TextField
              fullWidth label="Tenant address" margin="normal" autoFocus
              placeholder="mo-sffd.cad.watchcenter365.com"
              value={tenantInput} onChange={(e) => setTenantInput(e.target.value)}
            />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large">
              Continue
            </Button>
          </form>
        )}

        {!needsTenant && isNative() && (
          <Typography
            variant="caption" align="center" color="text.secondary"
            sx={{ display: 'block', mb: 1, cursor: 'pointer' }}
            onClick={() => { clearTenantBase(); setNeedsTenant(true); setError(''); }}
          >
            {getTenantBase().replace(/^https?:\/\//, '')} — change
          </Typography>
        )}

        {!needsTenant && totpStep === 'login' && (
          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Username" margin="normal" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
            <TextField fullWidth label="Password" type="password" margin="normal" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large">
              MDT Login
            </Button>
          </form>
        )}

        {totpStep === 'verify' && (
          <form onSubmit={handleTotpVerify}>
            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
              Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              fullWidth label="Authentication Code" margin="normal"
              value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus slotProps={{ htmlInput: { maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } } }}
            />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large" disabled={totpCode.length !== 6}>
              Verify
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => { setTotpStep('login'); setTotpCode(''); setError(''); }}>
              Back to Login
            </Button>
          </form>
        )}

        {totpStep === 'setup' && (
          <form onSubmit={handleTotpSetup}>
            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
              Set Up Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
            </Typography>
            {totpQr && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box component="img" src={totpQr} alt="TOTP QR Code" sx={{ width: 200, height: 200 }} />
              </Box>
            )}
            {totpSecret && (
              <Typography variant="caption" align="center" sx={{ display: 'block', mb: 2, wordBreak: 'break-all', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, fontFamily: 'monospace' }}>
                Manual entry key: {totpSecret}
              </Typography>
            )}
            <TextField
              fullWidth label="Enter code from app to verify" margin="normal"
              value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus slotProps={{ htmlInput: { maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } } }}
            />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large" disabled={totpCode.length !== 6}>
              Verify & Complete Setup
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => { setTotpStep('login'); setTotpCode(''); setError(''); }}>
              Back to Login
            </Button>
          </form>
        )}

      </Paper>
      {disclaimer && (
        <Paper
          sx={{
            p: 3, width: '100%', maxWidth: 720,
            fontSize: 12, lineHeight: 1.5, color: 'text.secondary',
            '& ul, & ol': { pl: 2.5, my: 0.5 }, '& h3': { m: 0, mb: 0.5, fontSize: 14 }, '& p': { m: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: disclaimer }}
        />
      )}
    </Box>
  );
}
